"use client";

import YouTubeVideoOverlay from "@/components/atoms/YouTubeVideoOverlay";
import {
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  VideoButtonClick,
  VideoModalClosed,
} from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { ArrowUpRight, Play } from "lucide-react";
import Image from "next/image";
import { useRef, useState } from "react";

export interface WalkThrough1Data {
  id?: string;
  status?: boolean;
  desktop_bg_image?: string;
  mobile_bg_image?: string;
  alt_image?: string;
  /** Line before the highlighted phrase */
  title_prefix?: string;
  /** Shown in `title_highlight_color` when set */
  title_highlight?: string;
  /** Text after the highlighted phrase */
  title_suffix?: string;
  title_highlight_color?: string;
  video_link?: string;
  cta_label?: string;
  /** "View Video" button background — defaults to white */
  cta_bg_color?: string;
  /** "View Video" button text/icon color — defaults to near-black */
  cta_text_color?: string;
  /** Scrim over the photo */
  overlay_color?: string;
  overlay_opacity?: number;
}

interface WalkThrough1Props {
  data: WalkThrough1Data | { data: WalkThrough1Data };
}

/** Figma Walk Through: scrim rgba(19, 5, 84, 0.82) */
const DEFAULT_OVERLAY = "#130554";
const DEFAULT_OVERLAY_OPACITY = 0.82;
/** "View Video" button defaults: white pill with near-black text */
const DEFAULT_CTA_BG = "#FFFFFF";
const DEFAULT_CTA_TEXT = "#171719";

function resolvePayload(
  data: WalkThrough1Props["data"],
): WalkThrough1Data | undefined {
  const anyData = data as { data?: WalkThrough1Data };
  if (anyData?.data && typeof anyData.data === "object") {
    return anyData.data as WalkThrough1Data;
  }
  return data as WalkThrough1Data;
}

const WalkThrough1 = ({ data }: WalkThrough1Props) => {
  const sectionData = resolvePayload(data);
  const [videoOpen, setVideoOpen] = useState(false);
  const ref = useRef<HTMLElement>(null);

  const {
    status = true,
    desktop_bg_image = "",
    mobile_bg_image = "",
    alt_image = "Campus walkthrough",
    title_prefix = "Walk Through ",
    title_highlight = "IIT Kharagpur",
    title_suffix = " – Virtually!",
    title_highlight_color = "#FFFFFF",
    video_link = "",
    cta_label = "View Video",
    cta_bg_color = DEFAULT_CTA_BG,
    cta_text_color = DEFAULT_CTA_TEXT,
    overlay_color = DEFAULT_OVERLAY,
    overlay_opacity = DEFAULT_OVERLAY_OPACITY,
  } = sectionData || {};

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current && status) {
  //     FireWidgetViewedEvent(ref, "walk_through_1");
  //   }
  // }, [status]);

  if (!status) return null;

  const hasBg = Boolean(desktop_bg_image || mobile_bg_image);

  const openVideo = () => {
    if (!video_link) return;
    VideoButtonClick({ label: title_highlight || "walk_through_video" });
    setVideoOpen(true);
  };

  const handleClose = () => {
    VideoModalClosed({ label: title_highlight || "walk_through_video" });
    setVideoOpen(false);
  };

  return (
    <section
      ref={ref}
      className={cn(
        "[&_*]:![font-family:Poppins,sans-serif]",
        "relative w-full overflow-hidden",
        "min-h-[280px] min-[640px]:min-h-[320px] min-[768px]:min-h-[360px]",
        !hasBg && "bg-[#130554]",
      )}
      style={{ fontFamily: "Poppins, sans-serif" }}
    >
      {hasBg && (
        <>
          <Image
            src={mobile_bg_image || desktop_bg_image}
            alt={alt_image}
            fill
            className="object-cover min-[768px]:hidden"
            sizes="100vw"
            priority={false}
          />
          <Image
            src={desktop_bg_image || mobile_bg_image}
            alt={alt_image}
            fill
            className="hidden object-cover min-[768px]:block"
            sizes="100vw"
            priority={false}
          />
        </>
      )}

      {/* Figma scrim: rgba(19, 5, 84, 0.82) — configurable via CMS */}
      <div
        className="absolute inset-0 z-[1]"
        style={{
          backgroundColor: overlay_color,
          opacity: overlay_opacity,
        }}
        aria-hidden
      />

      <div
        className={cn(
          "relative z-[2] flex flex-col items-center justify-center",
          "gap-[29px] px-6 py-10 min-[640px]:px-8 min-[640px]:py-12 min-[768px]:px-[41px] min-[768px]:py-[49px]",
          "min-h-[inherit]",
        )}
      >
        <div
          className={cn(
            "flex size-[60px] shrink-0 items-center justify-center rounded-full",
            "border-2 border-white/40 bg-transparent opacity-40",
          )}
          aria-hidden
        >
          <Play className="size-7 fill-white stroke-white" aria-hidden />
        </div>

        <h2
          className={cn(
            "max-w-[90vw] whitespace-normal text-center font-semibold leading-normal text-white",
            "text-[28px] min-[768px]:max-w-[826px] min-[768px]:whitespace-nowrap",
          )}
        >
          <span className="text-white">{title_prefix}</span>
          {title_highlight ? (
            <span style={{ color: title_highlight_color }}>{title_highlight}</span>
          ) : null}
          <span className="text-white">{title_suffix}</span>
        </h2>

        <button
          type="button"
          onClick={openVideo}
          disabled={!video_link}
          style={{ backgroundColor: cta_bg_color, color: cta_text_color }}
          className={cn(
            "inline-flex items-center gap-[10px] rounded-[6px] px-[18px] py-[12px]",
            "font-semibold text-base leading-normal",
            "transition hover:opacity-95",
            "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white",
            !video_link && "cursor-not-allowed opacity-50",
          )}
        >
          <span>{cta_label}</span>
          <ArrowUpRight
            className="size-6 shrink-0"
            style={{ color: cta_text_color }}
            aria-hidden
          />
        </button>
      </div>

      {video_link ? (
        <YouTubeVideoOverlay
          isOpen={videoOpen}
          onClose={handleClose}
          videoUrl={video_link}
        />
      ) : null}
    </section>
  );
};

export default WalkThrough1;
